import { calculateShipping } from '/Foundation/Common/code/Utils/CalculateShipping';

type ShippingMethod = {
  Name: string;
  FormattedPrice: string;
};

type ShippingOptionsResponse = {
  ShippingMethods?: ShippingMethod[] | null;
  Error?: { Error: boolean; Message?: string } | null;
  freeShippingConditionText?: string | null;
};

// Brazilian CEP, with or without the dash (20051-020 / 20051020).
const CEP_PATTERN = /^(\d{5})-?(\d{3})$/;

type ShippingProduct = { qty: number; sku: string };

const getShippingProducts = (): ShippingProduct[] => {
  const solutionIds = document.querySelector<HTMLInputElement>('.product-add-to-cart .datasul-ids-for-solution')?.value;
  const solutionProducts = solutionIds
    ?.split(',')
    .filter((solutionId) => solutionId)
    .map((solutionId) => ({ qty: 1, sku: solutionId }));

  if (solutionProducts?.length) return solutionProducts;

  const datasulId = document.getElementById('productBuyArea')?.dataset.datasulid;
  return datasulId ? [{ qty: 1, sku: datasulId }] : [];
};

const isFailureResponse = (result: ShippingOptionsResponse | null | undefined): boolean => {
  if (!result) return true;
  if (result.Error?.Error) return true;
  if (!result.ShippingMethods?.length) return true;
  return false;
};

const showInputError = (shippingCalcElem: Element) => {
  const errorMessage = shippingCalcElem.querySelector('.error-message');
  errorMessage?.classList.remove('d-none');
  shippingCalcElem.querySelector('.thule-input')?.classList.add('error');
  shippingCalcElem.querySelector('.trailing-icon')?.classList.remove('d-none');
};

const clearInputError = (shippingCalcElem: Element) => {
  const errorMessage = shippingCalcElem.querySelector('.error-message');
  if (errorMessage && !errorMessage.classList.contains('d-none')) {
    errorMessage.classList.add('d-none');
    shippingCalcElem.querySelector('.thule-input')?.classList.remove('error');
    shippingCalcElem.querySelector('.trailing-icon')?.classList.add('d-none');
  }
};

const hideResults = (resultsContainer: Element) => {
  resultsContainer.replaceChildren();
  if (!resultsContainer.classList.contains('d-none')) resultsContainer.classList.add('d-none');
};

const renderShippingOptions = (
  resultsContainer: Element,
  methods: ShippingMethod[],
  freeShippingConditionText?: string | null
) => {
  resultsContainer.replaceChildren();

  if (freeShippingConditionText) {
    const freeShipping = document.createElement('div');
    freeShipping.className = 'free-shipping-message';

    const text = document.createElement('p');
    text.className = 'paragraph--s fw-bold text-color--gray80';
    text.textContent = freeShippingConditionText;

    freeShipping.append(text);
    resultsContainer.append(freeShipping);
  }

  methods.forEach((method) => {
    const option = document.createElement('div');
    option.className = 'shipping-option';

    const price = document.createElement('span');
    price.className = 'heading6 mb-3';
    price.textContent = method.FormattedPrice;

    const name = document.createElement('p');
    name.className = 'paragraph--s text-color--gray80';
    name.textContent = method.Name;

    option.append(price, name);
    resultsContainer.append(option);
  });

  resultsContainer.classList.remove('d-none');
};

const setLoading = (button: HTMLButtonElement, btnText: Element, spinner: Element, loading: boolean) => {
  btnText.classList.toggle('d-none', loading);
  spinner.classList.toggle('d-none', !loading);
  button.disabled = loading;
  if (loading) {
    button.setAttribute('aria-busy', 'true');
  } else {
    button.removeAttribute('aria-busy');
  }
};

export const initShippingCalculator = (): void => {
  const shippingCalcElem = document.querySelector('.shipping_calculator');
  if (!shippingCalcElem) return;

  const shippingCalcInput = shippingCalcElem.querySelector('input');
  const shippingCalcBtn = shippingCalcElem.querySelector('#shippingCalcButton') as HTMLButtonElement | null;
  const btnText = shippingCalcBtn?.querySelector('.btn-text');
  const spinner = shippingCalcBtn?.querySelector('.spinner');
  const resultsContainer = shippingCalcElem.querySelector('.shipping-options-result');

  if (!shippingCalcInput || !shippingCalcBtn || !btnText || !spinner || !resultsContainer) return;

  shippingCalcBtn.addEventListener('click', async () => {
    setLoading(shippingCalcBtn, btnText, spinner, true);
    hideResults(resultsContainer);

    const cepMatch = CEP_PATTERN.exec(shippingCalcInput.value.trim());
    const products = getShippingProducts();

    if (!products.length || !cepMatch) {
      showInputError(shippingCalcElem);
      setLoading(shippingCalcBtn, btnText, spinner, false);
      return;
    }

    const zipCode = `${cepMatch[1]}-${cepMatch[2]}`;

    try {
      const result = (await calculateShipping(zipCode, products)) as ShippingOptionsResponse;

      if (isFailureResponse(result)) {
        showInputError(shippingCalcElem);
      } else {
        clearInputError(shippingCalcElem);
        renderShippingOptions(resultsContainer, result!.ShippingMethods!, result!.freeShippingConditionText);
      }
    } catch {
      showInputError(shippingCalcElem);
    } finally {
      setLoading(shippingCalcBtn, btnText, spinner, false);
    }
  });

  shippingCalcInput.addEventListener('input', () => clearInputError(shippingCalcElem));
};
